import { useFormik } from 'formik';
import Alert from '@mui/material/Alert';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import AddressCapture from '@components/AddressCapture/AddressCapture';
import { createAddressValidationSchema } from '@components/AddressCapture/AddressForm.schema';
import type { AddressFormState, NormalizedAddress } from '@components/AddressCapture/AddressCapture.types';
import { ManualAddressFallback } from '@components/AddressCapture/ManualAddressFallback';
import type { ResolvedAddress } from '@t/address.types';
import FormErrorMessage from '@shared/components/feedback/FormErrorMessage/FormErrorMessage';
import InfoBannerBlock from '@shared/components/feedback/InfoBannerBlock/InfoBannerBlock';
import { toNormalizedAddress } from '@utils/toNormalizedAddress';
import { withUnitNumber } from '@utils/withUnitNumber';

interface AddressFormProps {
  onSubmit: (address: NormalizedAddress) => void;
  onContactUs: () => void;
  onAddressChange?: () => void;
  onLookupUnavailable?: () => void;
  isLoading: boolean;
  addressState: AddressFormState;
  defaultAddress?: NormalizedAddress;
  forceUnitPrompt?: boolean;
}

const HEADING = 'Please enter your street address (e.g. 123 Main St.)';
const SUBHEADING = 'We need your address to find the best plans available in your area.';

const AddressForm = ({
  onSubmit,
  onContactUs,
  onAddressChange,
  onLookupUnavailable,
  isLoading,
  addressState,
  defaultAddress,
  forceUnitPrompt,
}: AddressFormProps) => {
  const { errors, touched, submitCount, setFieldValue, setFieldTouched, handleSubmit, values } = useFormik<{
    inputValue: string;
    resolvedAddress: ResolvedAddress | null;
    unitNumber: string;
  }>({
    enableReinitialize: true,
    validationSchema: createAddressValidationSchema(!!forceUnitPrompt),
    initialValues: {
      inputValue: defaultAddress?.displayAddress || '',
      resolvedAddress: defaultAddress ?? null,
      unitNumber: defaultAddress?.unitNumber ?? '',
    },
    onSubmit: (formValues) => {
      const base = formValues.resolvedAddress;
      if (!base) return;

      const trimmedUnit = formValues.unitNumber.trim();
      const withUnit: ResolvedAddress = trimmedUnit
        ? { ...base, unitNumber: trimmedUnit, displayAddress: withUnitNumber(base.displayAddress, trimmedUnit) }
        : base;

      onSubmit(toNormalizedAddress(withUnit));
    },
  });

  const showErrors = submitCount > 0;
  const showUnitInput = !!forceUnitPrompt;

  const handleAddressSelected = (address: ResolvedAddress): void => {
    // Echo the resolved address back into the tracked input value. Without this
    // an address that arrives without any typing — manual entry — would fail the
    // `inputValue` required rule and could never be submitted.
    setFieldValue('inputValue', address.displayAddress, false);
    setFieldValue('resolvedAddress', address, submitCount > 0);
    onAddressChange?.();
  };

  const handleInputChange = (value: string) => {
    setFieldValue('inputValue', value, false);
    setFieldValue('resolvedAddress', null, false);
    setFieldValue('unitNumber', '', false);
  };

  const heading = (
    <Stack gap="2px">
      <Typography variant="heading.subsection" color="border.black">
        {HEADING}
      </Typography>
      <Typography variant="body.md" color="border.black">
        {SUBHEADING}
      </Typography>
    </Stack>
  );

  if (addressState === 'lookup_unavailable') {
    return (
      <Box sx={{ padding: { xs: '20px', sm: '30px' }, display: 'flex', flexDirection: 'column', gap: '15px' }}>
        {heading}
        <ManualAddressFallback onSubmit={(address) => onSubmit(toNormalizedAddress(address))} isSubmitting={isLoading} />
      </Box>
    );
  }

  return (
    <Box sx={{ padding: { xs: '20px', sm: '30px' }, display: 'flex', flexDirection: 'column', gap: '15px' }}>
      {heading}

      <AddressCapture
        key={defaultAddress?.displayAddress ?? ''}
        onAddressSelected={handleAddressSelected}
        defaultInputValue={defaultAddress?.displayAddress}
        onInputChange={handleInputChange}
        onLookupUnavailable={onLookupUnavailable}
        error={showErrors ? errors.inputValue || (errors.resolvedAddress as string | undefined) : undefined}
      />

      <Collapse in={showUnitInput} timeout="auto" unmountOnExit>
        <Stack gap="30px" my="15px">
          <InfoBannerBlock description="It looks like your address has a unit. Please enter your apartment, unit, or suite number below so we can find the right plans for you." />

          <Stack gap="10px">
            <Typography variant="label.field" color="text.primary">
              Apartment, Unit, or Suite Number
            </Typography>
            <TextField
              variant="outlined"
              fullWidth
              placeholder="Enter number..."
              value={values.unitNumber}
              onBlur={() => setFieldTouched('unitNumber', true)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setFieldValue('unitNumber', e.target.value)}
              error={(touched.unitNumber || showErrors) && !!errors.unitNumber}
              FormHelperTextProps={{
                component: FormErrorMessage,
              }}
              helperText={(touched.unitNumber || showErrors) && errors.unitNumber ? errors.unitNumber : ''}
            />
          </Stack>
        </Stack>
      </Collapse>

      <Button
        variant="checkAvailabilityCta"
        onClick={() => handleSubmit()}
        disabled={isLoading || (showErrors && !values.resolvedAddress)}
        endIcon={isLoading ? <CircularProgress size={18} color="inherit" /> : undefined}
      >
        {!isLoading ? 'Check Availability' : ''}
      </Button>

      {addressState === 'active_account' && (
        <Alert severity="warning" icon={false} className="check-availability-inline-alert">
          <Typography
            component="p"
            variant="heading.subsection"
            sx={{ color: 'feedback.error', lineHeight: '130%', display: 'block', marginBottom: '10px' }}
          >
            In Progress Work Order Detected
          </Typography>
          <Typography component="p" variant="body.md" sx={{ color: 'border.black', lineHeight: '130%' }}>
            A work order is already in progress for the entered service address. System constraints prevent multiple
            active requests for the same location. Please contact a Customer Service Representative to modify your
            existing service or initiate a new request.
          </Typography>
        </Alert>
      )}

      {addressState === 'system_error' && (
        <Alert severity="error" icon={false} className="check-availability-inline-alert">
          <Typography
            component="p"
            variant="heading.subsection"
            sx={{ color: 'feedback.error', lineHeight: '130%', display: 'block', marginBottom: '10px' }}
          >
            Something Went Wrong
          </Typography>
          <Typography component="p" variant="body.md" sx={{ color: 'border.black', lineHeight: '130%' }}>
            We are having trouble finding your address.{' '}
            <Link
              onClick={onContactUs}
              underline="none"
              sx={{
                cursor: 'pointer',
                fontSize: 'inherit',
                fontWeight: 600,
                lineHeight: '130%',
                verticalAlign: 'baseline',
                color: 'interactive.link',
              }}
            >
              Click here to contact us.
            </Link>
          </Typography>
        </Alert>
      )}
    </Box>
  );
};

export default AddressForm;
